import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface HistoryFieldDiff {
  field: string;
  oldValue: unknown;
  newValue: unknown;
}

interface HistoryEntry {
  id: number;
  operation: "insert" | "update" | "delete";
  changedAt: string;
  changedByName: string;
  changedByRole: string | null;
  requestId: string | null;
  diff: HistoryFieldDiff[];
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  return String(value);
}

export function EnrollmentHistoryDialog({
  enrollmentId,
  open,
  onOpenChange,
}: {
  enrollmentId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: entries = [], isLoading } = useQuery<HistoryEntry[]>({
    queryKey: [`/api/enrollments/${enrollmentId}/history`],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/enrollments/${enrollmentId}/history`);
      return res.json();
    },
    enabled: open && enrollmentId != null,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-600" />
            Enrollment History
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground text-sm">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No history yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <div key={entry.id} className="border rounded-lg p-4">
                <div className="text-xs text-muted-foreground mb-3">
                  {new Date(entry.changedAt).toLocaleString()}
                  <span className="ml-2">
                    · Changed by: <span className="font-medium">{entry.changedByName}</span>
                    {entry.changedByRole && ` (${entry.changedByRole})`}
                  </span>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 text-xs space-y-1">
                  {entry.diff.map((d) => (
                    <div key={d.field} className="flex items-center gap-2">
                      <span className="font-medium capitalize">{d.field}:</span>
                      <span className="text-red-600 dark:text-red-400">{formatValue(d.oldValue)}</span>
                      <span className="text-gray-400">→</span>
                      <span className="text-green-600 dark:text-green-400">{formatValue(d.newValue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
